import { FLOW_THEMES } from '../../../components/QuestionFlow/themes/flowThemeRegistry';
import type { MediaAsset } from 'types';

import type { FlowMetadata, FlowQuestion } from './types';
import { FREQUENCY_OPTIONS, toOrderedQuestions } from './utils';

/**
 * The values a save would send for each field, mirroring the payload built when saving. Comparing these (not the raw
 * form state) keeps the module clean when, for example, quiz settings change while Quiz Mode is off.
 */
export const getComparableFlowValues = (metadata: FlowMetadata, hasRoleplayQuestion: boolean) => {
  const isQuiz = !hasRoleplayQuestion && metadata.is_quiz === true;
  const quizMode =
    metadata.quiz_mode
    ?? (metadata.is_quiz === true && metadata.passing_score_percentage === null ? 'pass_only' : 'graded');
  const passOnly = isQuiz && quizMode === 'pass_only';
  const toNumber = (value: unknown) =>
    value === null || value === undefined || value === '' ? null : Number(value);
  return {
    name: (metadata.name ?? '').trim(),
    title: (metadata.title ?? '').trim(),
    description: metadata.description ?? '',
    estimated_duration_minutes: String(metadata.estimated_duration_minutes ?? '').trim(),
    frequency: (metadata.frequency ?? '').trim(),
    is_public: metadata.is_public ?? false,
    is_active: metadata.is_active ?? true,
    is_quiz: isQuiz,
    quiz_mode: isQuiz ? quizMode : null,
    passing_score_percentage: isQuiz && !passOnly ? toNumber(metadata.passing_score_percentage) : null,
    max_attempts: isQuiz ? (passOnly ? 1 : toNumber(metadata.max_attempts)) : null,
    allow_retake_after_pass: isQuiz && !passOnly ? metadata.allow_retake_after_pass ?? false : false,
    randomize_question_order: isQuiz ? metadata.randomize_question_order ?? true : true,
    randomize_option_order: isQuiz ? metadata.randomize_option_order ?? true : true,
    voice_mode_enabled: metadata.voice_mode_enabled === true,
    theme: metadata.theme ?? '',
    media_asset_id: metadata.media?.id ?? metadata.media_asset_id ?? null,
  };
};

type ComparableFlowValues = ReturnType<typeof getComparableFlowValues>;
type ComparableField = keyof ComparableFlowValues;

export const FLOW_FIELD_LABELS: Record<ComparableField, string> = {
  name: 'Name',
  title: 'Learner-facing title',
  description: 'Description',
  estimated_duration_minutes: 'Estimated duration (minutes)',
  frequency: 'Frequency',
  is_public: 'Public Module',
  is_active: 'Active',
  is_quiz: 'Quiz Mode',
  quiz_mode: 'Assessment mode',
  passing_score_percentage: 'Passing score (%)',
  max_attempts: 'Max attempts',
  allow_retake_after_pass: 'Allow retake after passing',
  randomize_question_order: 'Randomize question order',
  randomize_option_order: 'Randomize option order',
  voice_mode_enabled: 'Voice Mode',
  theme: 'Theme',
  media_asset_id: 'Module image',
};

// Only meaningful while the module is a quiz; turning Quiz Mode off is already reported by its own row.
const QUIZ_DETAIL_FIELDS = new Set<ComparableField>([
  'quiz_mode',
  'passing_score_percentage',
  'max_attempts',
  'allow_retake_after_pass',
  'randomize_question_order',
  'randomize_option_order',
]);

export type FlowFieldChange = {
  field: ComparableField;
  label: string;
  from: string;
  to: string;
};

const mediaLabel = (media: MediaAsset | null | undefined, id: unknown) => {
  if (!id) {
    return 'No image';
  }
  const alt = media?.alt_text?.trim();
  if (alt) {
    return alt;
  }
  const fileName = media?.storage_key?.split('/').pop();
  return fileName || 'Selected image';
};

const formatValue = (
  field: ComparableField,
  value: ComparableFlowValues[ComparableField],
  values: ComparableFlowValues,
  metadata: FlowMetadata,
): string => {
  if (QUIZ_DETAIL_FIELDS.has(field) && !values.is_quiz) {
    return '—';
  }
  switch (field) {
    case 'frequency':
      return FREQUENCY_OPTIONS.find((option) => option.value === value)?.label ?? (String(value ?? '') || 'Not set');
    case 'quiz_mode':
      return value === 'pass_only' ? 'Pass-Only Survey' : 'Graded Quiz';
    case 'passing_score_percentage':
      return value === null ? 'Not set' : `${value}%`;
    case 'max_attempts':
      return value === null ? 'Unlimited' : String(value);
    case 'estimated_duration_minutes':
      return value === '' ? 'Not set' : `${value} min`;
    case 'theme':
      return value
        ? FLOW_THEMES.find((theme) => theme.id === value)?.label ?? String(value)
        : 'Inherit from course';
    case 'media_asset_id':
      return mediaLabel(metadata.media, value);
    default:
      if (typeof value === 'boolean') {
        return value ? 'On' : 'Off';
      }
      return String(value ?? '').trim() ? String(value) : 'Empty';
  }
};

/** Field-by-field changes between the loaded module and the draft, with human labels and display values. */
export const diffFlowMetadata = (
  before: FlowMetadata,
  after: FlowMetadata,
  { beforeHasRoleplay = false, afterHasRoleplay = false }: { beforeHasRoleplay?: boolean; afterHasRoleplay?: boolean } = {},
): FlowFieldChange[] => {
  const beforeValues = getComparableFlowValues(before, beforeHasRoleplay);
  const afterValues = getComparableFlowValues(after, afterHasRoleplay);
  return (Object.keys(FLOW_FIELD_LABELS) as ComparableField[])
    .filter((field) => !Object.is(beforeValues[field], afterValues[field]))
    .filter((field) => !QUIZ_DETAIL_FIELDS.has(field) || afterValues.is_quiz)
    .map((field) => ({
      field,
      label: FLOW_FIELD_LABELS[field],
      from: formatValue(field, beforeValues[field], beforeValues, before),
      to: formatValue(field, afterValues[field], afterValues, after),
    }));
};

/** Order, membership and required flags: everything set-questions would change. */
export const questionListSignature = (items: FlowQuestion[] | null | undefined) =>
  toOrderedQuestions(items)
    .map((item) => `${item.question_id}:${item.is_required ? 1 : 0}`)
    .join(',');
